import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@vnetwork/ui/data';
import { Skeleton } from '@vnetwork/ui/primitives';
import { Building2 } from 'lucide-react';
import { WidgetCard } from '@/pages/dashboard/widgets/WidgetCard';
import type { TopOrgRow } from '@/api/operations-org-hooks';

interface Props {
  topOrgs: TopOrgRow[] | undefined;
  isLoading: boolean;
}

/**
 * Top-10 orgs by service count widget. Each row is clickable — navigates to
 * /operations/orgs/:orgId. Customer name (or raw org ID when no DC record) is
 * shown with the service count badge on the right.
 */
export function OpsTopOrgsWidget({ topOrgs, isLoading }: Props) {
  const navigate = useNavigate();

  return (
    <WidgetCard title="Top organizations" description="By active service count" bodyPadding={false}>
      {isLoading ? (
        <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 36 }} />
          ))}
        </div>
      ) : !topOrgs?.length ? (
        <div style={{ padding: 'var(--vn-space-4)' }}>
          <EmptyState
            icon={<Building2 size={20} />}
            title="No org data"
            description="No organizations with services yet."
          />
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {topOrgs.map((org, idx) => {
            const displayName =
              org.customer?.customer_company_name ||
              org.customer?.customer_name ||
              `Org ${org.organization_id}`;
            return (
              <li
                key={org.organization_id}
                onClick={() => navigate(`/operations/orgs/${org.organization_id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-3)',
                  padding: 'var(--vn-space-3) var(--vn-space-4)',
                  cursor: 'pointer',
                  borderBottom:
                    idx < topOrgs.length - 1 ? '1px solid var(--vn-border-subtle)' : 'none',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'var(--vn-bg-hover)')
                }
                onMouseLeave={(e) => (e.currentTarget.style.background = '')}
              >
                {/* Rank badge */}
                <span
                  style={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--vn-bg-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--vn-font-size-xs)',
                    fontWeight: 600,
                    color: 'var(--vn-fg-muted)',
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </span>

                {/* Name */}
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 'var(--vn-font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--vn-fg-default)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </span>

                {/* Service count */}
                <span
                  style={{
                    fontSize: 'var(--vn-font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--vn-primary-solid)',
                    flexShrink: 0,
                  }}
                >
                  {org.service_count.toLocaleString('en-US')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetCard>
  );
}
